import React, { useRef, useEffect, useState, useMemo } from 'react';

import { formatDateTime } from '../../services/date';
import { DataFE, Line } from '../../../types';

const axesColor = "#ffffff11";
const lineColor = "#fff";
const labelColor = "#fff";

const positiveColor = "#009933";
const negativeColor = "#cc0000";

const lineWidth = 1;

const paddingTop = 10;
const paddingLeft = 80;
const paddingBottom = 40;
const heightBottomChart = 100;

type ChartProps = {
    height: number,
    stock?: DataFE,
    lines?: Line[],
    isYAxisPercent?: boolean,
    defaultSelect?: null | [number, number],
    onSelect?: (select: null | [number, number]) => void,
};

export default function Chart({ height, stock, lines, isYAxisPercent, defaultSelect, onSelect }: ChartProps) {
    const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);

    const [mouseX, setMouseX] = useState(0);
    const [select, setSelect] = useState<null | [number, number]>(null);

    const [scaleXBottom, scaleYBottom] = useMemo(() => {
        const width = canvas?.parentElement?.offsetWidth || 600;

        let minX = Number.MAX_VALUE;
        let maxX = Number.MIN_VALUE;
        let minY = Number.MAX_VALUE;
        let maxY = Number.MIN_VALUE;

        if (stock) {
            for (let i = 0; i < stock.date.length; i++) {
                minX = Math.min(minX, stock.date[i]);
                maxX = Math.max(maxX, stock.date[i]);
                minY = Math.min(minY, stock.low[i]);
                maxY = Math.max(maxY, stock.high[i]);
            }
        }

        if (lines) {
            lines.forEach(({ x1, x2, y1, y2 }) => {
                minX = Math.min(minX, x1 ?? minX, x2 ?? minX);
                maxX = Math.max(maxX, x1 ?? maxX, x2 ?? maxX);
                minY = Math.min(minY, y1 ?? minY, y2 ?? minY);
                maxY = Math.max(maxY, y1 ?? maxY, y2 ?? maxY);
            });
        }

        const scaleX = scale([minX, maxX], [paddingLeft, width]);
        const scaleY = scale([minY, maxY], [height - paddingBottom, height - paddingBottom - heightBottomChart]);

        return [scaleX, scaleY];
    }, [canvas, height, stock, lines]);

    const [scaleX, scaleY] = useMemo(() => {
        if (!select) {
            const scaleX = scale([scaleXBottom.minDomain, scaleXBottom.maxDomain], [scaleXBottom.minRange, scaleXBottom.maxRange]);
            const scaleY = scale([scaleYBottom.minDomain, scaleYBottom.maxDomain], [height - paddingBottom - heightBottomChart - paddingBottom, paddingTop]);

            return [scaleX, scaleY];

        } else {
            const minX = scaleXBottom.getDomain(select[0]);
            const maxX = scaleXBottom.getDomain(select[1]);
            let minY = Number.MAX_VALUE;
            let maxY = Number.MIN_VALUE;

            if (stock) {
                for (let i = 0; i < stock.date.length; i++) {
                    if (stock.date[i] >= minX && stock.date[i] <= maxX) {
                        minY = Math.min(minY, stock.low[i]);
                        maxY = Math.max(maxY, stock.high[i]);
                    }
                }
            }

            if (!stock && lines) {
                lines.forEach(({ x1, x2, y1, y2 }) => {
                    if (y1) {
                        minY = Math.min(minY, y1);
                        maxY = Math.max(maxY, y1);
                    }
                    if (y2) {
                        minY = Math.min(minY, y2);
                        maxY = Math.max(maxY, y2);
                    }
                });
            }

            const scaleX = scale([minX, maxX], [scaleXBottom.minRange, scaleXBottom.maxRange]);
            const scaleY = scale([minY, maxY], [height - paddingBottom - heightBottomChart - paddingBottom, paddingTop]);

            return [scaleX, scaleY];
        }

    }, [canvas, height, stock, lines, select, scaleXBottom, scaleYBottom]);

    useEffect(() => {
        if (!defaultSelect) {
            setSelect(null);
        } else {
            setSelect([scaleXBottom.getRange(defaultSelect[0]), scaleXBottom.getRange(defaultSelect[1])]);
        }
    }, [defaultSelect, scaleXBottom]);

    const mouseDownRef = useRef(0);

    function handleMouseDown(e: React.MouseEvent<HTMLCanvasElement, MouseEvent>) {
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        mouseDownRef.current = x;
    }

    function handleMouseMove(e: React.MouseEvent<HTMLCanvasElement, MouseEvent>) {
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const movementX = e.movementX;

        const isBottom = y > rect.height - paddingBottom - heightBottomChart;

        if (isBottom) {
            if (mouseDownRef.current) {
                // Select on bottom chart
                if (mouseDownRef.current === x) {
                    setSelect(null);
                } else {
                    setSelect([Math.min(mouseDownRef.current, x), Math.max(mouseDownRef.current, x)]);
                }
            }
        } else {
            // for tooltip
            setMouseX(x);

            if (mouseDownRef.current) {
                // Move on top chart
                setSelect(select => {
                    if (!select) return null;
                    const [x1, x2] = select;
                    const moveDomain = scaleX.getDomain(0) - scaleX.getDomain(movementX);
                    const moveRangeBottom = scaleXBottom.getRange(0) - scaleXBottom.getRange(moveDomain);

                    return [x1 - moveRangeBottom, x2 - moveRangeBottom];
                });
            }
        }
    }

    function handleMouseUp(e: React.MouseEvent<HTMLCanvasElement, MouseEvent>) {
        if (!canvas) return;
        if (!mouseDownRef.current) return;
        if (!select) return;

        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const movementX = e.movementX;

        const isBottom = y > rect.height - paddingBottom - heightBottomChart;

        if (isBottom) {
            if (mouseDownRef.current === x) {
                setSelect(null);
                onSelect?.(null);
            } else {
                const select: [number, number] = [Math.min(mouseDownRef.current, x), Math.max(mouseDownRef.current, x)];
                setSelect(select);
                onSelect?.([
                    scaleXBottom.getDomain(select[0]),
                    scaleXBottom.getDomain(select[1]),
                ]);
            }
        }

        mouseDownRef.current = 0;
    }

    useEffect(() => {
        if (!canvas) return;

        function handleMouseWheel(e: WheelEvent) {
            if (!canvas) return;

            e.preventDefault();

            const move = e.deltaY;

            // ZOOM
            setSelect(select => {
                if (!select) return null;
                const [x1, x2] = select;
                const middle = (x1 + x2) / 2;

                const diff = Math.abs(x2 - x1) / 2;
                const diffNew = move > 0 ? diff / 1.2 : diff * 1.2;

                return [middle - diffNew, middle + diffNew];
            });
        }

        canvas.addEventListener("wheel", handleMouseWheel, { passive: false });

        return () => {
            canvas.removeEventListener("wheel", handleMouseWheel);
        }
    }, [canvas]);

    // tooltip
    const [tooltipX, tooltipContent] = useMemo(() => {
        if (!stock && !lines) return [0, []];

        let tooltipX = 0;
        let tooltipDate = scaleX.getDomain(mouseX);
        const tooltipContent: { title: string, value: string }[] = [];

        if (stock) {
            let indexClosest = 0;
            let distanceClosest = Math.abs(mouseX - scaleX.getRange(stock.date[0]));
            for (let i = 1; i < stock.date.length; i++) {
                const distance = Math.abs(mouseX - scaleX.getRange(stock.date[i]));
                if (distance < distanceClosest) {
                    indexClosest = i;
                    distanceClosest = distance;
                }
            };

            tooltipDate = stock.date[indexClosest];
            tooltipContent.push({ title: "Date", value: formatDateTime(stock.date[indexClosest]) });
            tooltipContent.push({ title: "High", value: stock.high[indexClosest].toLocaleString() });
            tooltipContent.push({ title: "Open", value: stock.open[indexClosest].toLocaleString() });
            tooltipContent.push({ title: "Close", value: stock.close[indexClosest].toLocaleString() });
            tooltipContent.push({ title: "Low", value: stock.low[indexClosest].toLocaleString() });
        }

        if (lines) {
            for (let i = 0; i < lines.length; i++) {
                const { x1, x2, y1, y2, color, label } = lines[i];
                if (!is(x1) && !is(x2) && is(y1)) {
                    tooltipContent.push({ title: label || color || "Line", value: formatYValue(y1, isYAxisPercent) });
                }
                if (is(x1) && !is(x2) && x1 === tooltipDate) {
                    tooltipContent.push({ title: label || color || "Line", value: formatDateTime(x1) });
                }
                if (is(x1) && is(x2) && is(y1) && is(y2) && ((x1 <= tooltipDate && tooltipDate <= x2) || (x2 <= tooltipDate && tooltipDate <= x1))) {
                    tooltipContent.push({ title: label || color || "Line", value: `${formatDateTime(x1)} - ${formatYValue(y1, isYAxisPercent)}\n${formatDateTime(x2)} - ${formatYValue(y2, isYAxisPercent)}` });
                }
            }
        }

        return [scaleX.getRange(tooltipDate), tooltipContent];
    }, [mouseX, stock, lines, scaleX, isYAxisPercent]);

    // render
    useEffect(() => {
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const parentWidth = canvas.parentElement?.offsetWidth || 600;
        canvas.width = parentWidth;
        canvas.height = height;

        // Vyčištění canvasu
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Vykreslení os
        ctx.beginPath();
        ctx.strokeStyle = labelColor;
        ctx.lineWidth = lineWidth;

        // Osa Y
        ctx.moveTo(scaleX.minRange, scaleY.minRange);
        ctx.lineTo(scaleX.minRange, scaleY.maxRange);
        ctx.moveTo(scaleXBottom.minRange, scaleYBottom.minRange);
        ctx.lineTo(scaleXBottom.minRange, scaleYBottom.maxRange);

        // Osa X
        ctx.moveTo(scaleX.minRange, scaleY.minRange);
        ctx.lineTo(scaleX.maxRange, scaleY.minRange);
        ctx.moveTo(scaleXBottom.minRange, scaleYBottom.minRange);
        ctx.lineTo(scaleXBottom.maxRange, scaleYBottom.minRange);

        ctx.stroke();


        // Show axes labels (Top Chart)
        ctx.beginPath();
        ctx.font = '12px Arial';
        ctx.fillStyle = labelColor;
        ctx.strokeStyle = axesColor;

        // Y-axis labels (Top Chart)
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';

        let stepY = (scaleY.maxDomain - scaleY.minDomain) / 10;
        // if (stepY < 25) stepY = 25;
        if (stepY !== 0)
            for (let domainValue = scaleY.minDomain; domainValue <= scaleY.maxDomain; domainValue += stepY) {
                const rangeY = scaleY.getRange(domainValue);
                ctx.fillText(formatYValue(domainValue, isYAxisPercent), scaleX.minRange - 5, rangeY);
                ctx.moveTo(scaleX.minRange, rangeY);
                ctx.lineTo(scaleX.maxRange, rangeY);
            }


        // X-axis labels (Top Chart)
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';

        let stepX = (scaleX.maxDomain - scaleX.minDomain) / 10;
        // if (stepX < 100) stepX = 100;
        if (stepX !== 0)
            for (let domainValue = scaleX.minDomain; domainValue <= scaleX.maxDomain; domainValue += stepX) {
                const rangeX = scaleX.getRange(domainValue);
                ctx.fillText(formatDateTime(domainValue), rangeX, scaleY.minRange + 5);
                ctx.moveTo(rangeX, scaleY.minRange);
                ctx.lineTo(rangeX, scaleY.maxRange);
            }

        // Y-axis labels (Top Chart)
        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';

        let stepYBottom = (scaleYBottom.maxDomain - scaleYBottom.minDomain) / 5;

        // if (stepYBottom < 25) stepY = 25;
        if (stepYBottom !== 0)
            for (let domainValue = scaleYBottom.minDomain; domainValue <= scaleYBottom.maxDomain; domainValue += stepYBottom) {
                const rangeYBottom = scaleYBottom.getRange(domainValue);
                ctx.fillText(formatYValue(domainValue, isYAxisPercent), scaleXBottom.minRange - 5, rangeYBottom);
                ctx.moveTo(scaleXBottom.minRange, rangeYBottom);
                ctx.lineTo(scaleXBottom.maxRange, rangeYBottom);
            }

        // X-axis labels (Top Chart)
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';

        let stepXBottom = (scaleXBottom.maxDomain - scaleXBottom.minDomain) / 10;
        // if (stepXBottom < 100) stepXBottom = 100;
        if (stepXBottom !== 0)
            for (let domainValue = scaleXBottom.minDomain; domainValue <= scaleXBottom.maxDomain; domainValue += stepXBottom) {
                const rangeXBottom = scaleXBottom.getRange(domainValue);
                ctx.fillText(formatDateTime(domainValue), rangeXBottom, scaleYBottom.minRange + 5);
                ctx.moveTo(rangeXBottom, scaleYBottom.minRange);
                ctx.lineTo(rangeXBottom, scaleYBottom.maxRange);
            }
        ctx.stroke();

        if (stock) {
            ctx.lineCap = "butt";
            ctx.lineWidth = lineWidth;

            for (let i = 0; i < stock.date.length; i++) {
                const date = stock.date[i];
                const open = stock.open[i];
                const close = stock.close[i];
                const high = stock.high[i];
                const low = stock.low[i];

                const color = open < close ? positiveColor : negativeColor;
                ctx.strokeStyle = color;

                const x = scaleX.getRange(date);

                if (x > paddingLeft && x < canvas.width) {
                    const yOpen = scaleY.getRange(open);
                    const yClose = scaleY.getRange(close);
                    const yHigh = scaleY.getRange(high);
                    const yLow = scaleY.getRange(low);

                    ctx.beginPath();
                    ctx.lineWidth = lineWidth;
                    ctx.moveTo(x, yLow);
                    ctx.lineTo(x, yHigh);
                    ctx.stroke();

                    ctx.beginPath();
                    ctx.lineWidth = 6;
                    ctx.moveTo(x, yOpen === yClose ? yOpen + 1 : yOpen);
                    ctx.lineTo(x, yClose);
                    ctx.stroke();
                }

                const xBottom = scaleXBottom.getRange(date);
                const yLowBottom = scaleYBottom.getRange(low);
                const yHighBottom = scaleYBottom.getRange(high);

                ctx.beginPath();
                ctx.lineWidth = lineWidth;
                ctx.moveTo(xBottom, yLowBottom);
                ctx.lineTo(xBottom, yHighBottom);
                ctx.stroke();
            }
        }

        // Vykreslení čáry
        if (lines) {

            const regionTop = new Path2D();
            regionTop.rect(scaleX.minRange, scaleY.minRange, scaleX.maxRange - scaleX.minRange, scaleY.maxRange - scaleY.minRange);

            ctx.save();
            ctx.clip(regionTop);

            lines.forEach(line => {
                ctx.beginPath();
                const x1 = line.x1 ?? scaleXBottom.minDomain;
                const x2 = line.x2 ?? line.x1 ?? scaleXBottom.maxDomain;
                const y1 = line.y1 ?? scaleYBottom.minDomain;
                const y2 = line.y2 ?? line.y1 ?? scaleYBottom.maxDomain;

                ctx.strokeStyle = line.color ?? lineColor;
                ctx.lineWidth = line.width ?? lineWidth;

                ctx.moveTo(scaleX.getRange(x1), scaleY.getRange(y1));
                ctx.lineTo(scaleX.getRange(x2), scaleY.getRange(y2));

                ctx.stroke();
            });

            ctx.restore();

            lines.forEach(line => {
                ctx.beginPath();
                const x1 = line.x1 ?? scaleXBottom.minDomain;
                const x2 = line.x2 ?? line.x1 ?? scaleXBottom.maxDomain;
                const y1 = line.y1 ?? scaleYBottom.minDomain;
                const y2 = line.y2 ?? line.y1 ?? scaleYBottom.maxDomain;

                ctx.strokeStyle = line.color ?? lineColor;
                ctx.lineWidth = line.width ?? lineWidth;

                ctx.moveTo(scaleXBottom.getRange(x1), scaleYBottom.getRange(y1));
                ctx.lineTo(scaleXBottom.getRange(x2), scaleYBottom.getRange(y2));

                ctx.stroke();
            });
        }

    }, [canvas, height, stock, lines, scaleX, scaleY, scaleXBottom, scaleYBottom, isYAxisPercent]);

    return <div className="block">
        <div className="chart">
            <canvas
                ref={c => setCanvas(c)}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
            />
            {select && <div
                className="select"
                style={{
                    bottom: paddingBottom + 4,
                    height: heightBottomChart,
                    left: `${select[0]}px`,
                    width: `${select[1] - select[0]}px`,
                }}
            />}
            {tooltipX && <div className="tooltip" style={{ left: Math.round(tooltipX), height: height - paddingBottom - heightBottomChart - paddingBottom }}>
                <table className={tooltipX < (canvas?.width || 0) / 2 ? "tooltip-left block" : "tooltip-right block"}>
                    <tbody>
                        {tooltipContent.map(({ title, value }, i) => <tr key={i}>
                            <td>{title}</td>
                            <td>{value}</td>
                        </tr>)}
                    </tbody>
                </table>
            </div>}
        </div>
    </div>;
};

export function scale(domain: [number, number], range: [number, number]) {
    const [minDomain, maxDomain] = domain;
    const [minRange, maxRange] = range;

    const getRange = (value: number) => {
        return ((value - minDomain) / (maxDomain - minDomain)) * (maxRange - minRange) + minRange;
    }

    const getDomain = (value: number) => {
        return ((value - minRange) / (maxRange - minRange)) * (maxDomain - minDomain) + minDomain;
    }

    return {
        getDomain,
        getRange,
        minDomain, maxDomain,
        minRange, maxRange
    };
}

function is<T>(v: T | null | undefined): v is T {
    return v !== null && v !== undefined;
}

function formatYValue(value: number, isYAxisPercent?: boolean) {
    return isYAxisPercent ? (value * 100).toFixed(2) + " %" : value.toLocaleString();
}
